import type { ButtonHTMLAttributes, ReactNode } from "react";
import type { MetaIcon } from "../constants/icon.constant";
import SolidIconBuilder from "../helpers/SolidIconBuilder";

type Variant = "primary" | "secondary" | "outlined" | "icon";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: Variant;
  disabled?: boolean;
  loading?: boolean;
  fluid?: boolean;
  className?: string;
  icon?: MetaIcon;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    "px-10 py-2 font-light text-black rounded-full bg-gradient-to-bl from-primary-200 via-primary-300 to-primary-400 transition hover:cursor-pointer hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed",
  secondary:
    "px-4 py-2 font-light text-white rounded-full bg-sidebar-active hover:cursor-pointer hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed",
  outlined:
    "px-4 py-2 font-light text-white/40 border rounded-full bg-transparent hover:cursor-pointer hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed",
  icon: "hover:bg-sidebar-active hover:cursor-pointer rounded-full disabled:opacity-50 disabled:cursor-not-allowed p-3",
};

export default function Button({
  children,
  variant = "primary",
  disabled = false,
  loading = false,
  fluid = false,
  icon,
  className = "",
  ...props
}: Readonly<ButtonProps>) {
  const classes = [
    "self-center text-sm font-medium transition-all flex items-center justify-center gap-2 whitespace-nowrap text-nowrap",
    fluid ? "w-full" : "w-auto",
    VARIANTS[variant],
    disabled || loading ? "cursor-not-allowed opacity-70" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button className={classes} disabled={disabled || loading} {...props}>
      {loading && (
        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
      )}
      <div className="flex items-center gap-3 justify-center">
        {icon && (
          <SolidIconBuilder
            icon={icon}
            color={variant === "outlined" ? "#707170" : "#000"}
          />
        )}
        {children}
      </div>
    </button>
  );
}
